import { ColumnType } from './types';

/**
 * Formata um valor baseado no tipo da coluna
 */
export const formatValue = (value: any, type: ColumnType): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  switch (type) {
    case 'text':
      return String(value);
    
    case 'number':
      return Number(value).toLocaleString('pt-BR');
    
    case 'money':
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(Number(value));
    
    case 'date':
      if (typeof value === 'string' || value instanceof Date) {
        const date = new Date(value);
        return date.toLocaleDateString('pt-BR');
      }
      return String(value);
    
    case 'datetime':
      if (typeof value === 'string' || value instanceof Date) {
        const date = new Date(value);
        return date.toLocaleString('pt-BR');
      }
      return String(value);
    
    case 'boolean':
      return value ? 'Sim' : 'Não';
    
    case 'badge':
      return String(value);
    
    case 'custom':
    default:
      return String(value);
  }
};

/**
 * Normaliza uma string removendo acentos e caracteres especiais
 */
export const normalizeString = (str: string): string => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove acentos
    .replace(/[\.\-\/\(\)\s]/g, '') // Remove pontuação e espaços
    .toLowerCase();
};

/**
 * Realiza busca em um array de objetos
 */
export const searchInData = (data: any[], searchTerm: string): any[] => {
  if (!searchTerm.trim()) {
    return data;
  }

  const normalizedSearch = normalizeString(searchTerm);
  
  return data.filter(item => {
    return Object.values(item).some(value => {
      if (value === null || value === undefined) return false;
      
      const normalizedValue = normalizeString(String(value));
      return normalizedValue.includes(normalizedSearch);
    });
  });
};

/**
 * Ordena um array de objetos baseado em um campo e direção
 */
export const sortData = (data: any[], field: string, direction: 'asc' | 'desc'): any[] => {
  return [...data].sort((a, b) => {
    const aValue = a[field];
    const bValue = b[field];
    
    // Trata valores nulos
    if (aValue === null || aValue === undefined) return direction === 'asc' ? 1 : -1;
    if (bValue === null || bValue === undefined) return direction === 'asc' ? -1 : 1;
    
    // Comparação para números
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }
    
    // Comparação para datas
    if (aValue instanceof Date && bValue instanceof Date) {
      return direction === 'asc' 
        ? aValue.getTime() - bValue.getTime()
        : bValue.getTime() - aValue.getTime();
    }
    
    // Comparação para strings (padrão)
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();
    
    if (direction === 'asc') {
      return aStr.localeCompare(bStr, 'pt-BR');
    } else {
      return bStr.localeCompare(aStr, 'pt-BR');
    }
  });
};

/**
 * Pagina um array de dados
 */
export const paginateData = (data: any[], page: number, pageSize: number): any[] => {
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  return data.slice(startIndex, endIndex);
};

/**
 * Calcula informações de paginação
 */
export const calculatePaginationInfo = (total: number, page: number, pageSize: number) => {
  const totalPages = Math.ceil(total / pageSize);
  const startItem = total === 0 ? 0 : ((page - 1) * pageSize) + 1;
  const endItem = Math.min(page * pageSize, total);
  
  return {
    totalPages,
    startItem,
    endItem,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
};
